import { addPointModeAtom, pickedPointAtom, pointCloudTargetsAtom, userViewerSettingsAtom } from '@/atoms';
import { GetProjectQuery } from '@/graphql/graphql';
import { useLayerSettingsMany } from '@/hooks/useLayerSettings';
import {
  IClassification,
  IPointCloudTreeNode,
  PointCloudMaterial,
  PointCloudOctree,
  PointCloudOctreeNode,
  PointColorType,
  PointOpacityType,
  PointShape,
  PointSizeType,
  Potree,
  QueueItem,
} from '@pnext/three-loader';
import { NodeLoader } from '@pnext/three-loader/build/declarations/loading2/octree-loader';
import type { CameraControls } from '@react-three/drei';
import { useFrame, useThree } from '@react-three/fiber';
import { useAtomValue, useSetAtom } from 'jotai';
import { useEffect, useMemo, useRef, useState, type RefObject } from 'react';
import * as THREE from 'three';
import { LayerData } from '../atoms/layers.atom';
import { PointCloudBoundingBoxes } from './3D/PointCloudBoundingBoxes';
import PointX from './3D/PointX';
import { AddPointTool } from './AddPointTool';

export const PointCloud = ({
  layers,
  visible,
  project,
}: {
  layers: LayerData[];
  visible?: boolean;
  controlRef: RefObject<CameraControls | null>;
  project?: GetProjectQuery['project'];
}) => {
  const viewerSettings = useAtomValue(userViewerSettingsAtom);
  const layerSettings = useLayerSettingsMany(layers.map((l) => l.id));
  const scene = useThree((state) => state.scene);
  const camera = useThree((state) => state.camera);
  const renderer = useThree((state) => state.gl);
  const pickedPoint = useAtomValue(pickedPointAtom);
  const setPointCloudTargets = useSetAtom(pointCloudTargetsAtom);

  // Latest loading-related settings, read by the captured updateChildVisibility
  // closure on each call. Using a ref avoids re-creating the Potree instance
  // (which would force every loaded cloud to reload) when the user nudges sliders.
  const loadingSettingsRef = useRef({
    loadingDistance: viewerSettings?.pointCloudLoadingDistance ?? 0,
  });
  useEffect(() => {
    loadingSettingsRef.current.loadingDistance = viewerSettings?.pointCloudLoadingDistance ?? 0;
  }, [viewerSettings?.pointCloudLoadingDistance]);

  // We support both Potree v1 (cloud.js + .bin per-node) and v2 (metadata.json
  // + hierarchy). Each version needs its own Potree instance because the
  // geometry loader is chosen at construction time; LRU and point budget are
  // managed per-instance, so clouds are grouped by version at update time.
  const potrees = useMemo(() => {
    // Scratch state — boundingSphere coords live in the pointcloud's local
    // space (cameraPosition is already transformed by the library), so we
    // build the frustum in that same space per call.
    const cullMatrix = new THREE.Matrix4();
    const cullFrustum = new THREE.Frustum();

    const updateChildVisibility = function (
      queueItem: QueueItem,
      // @ts-expect-error temp fix
      priorityQueue: BinaryHeap<QueueItem>,
      pointCloud: PointCloudOctree,
      node: IPointCloudTreeNode,
      cameraPosition: THREE.Vector3,
      camera: THREE.Camera,
      halfHeight: number,
    ): void {
      const { loadingDistance } = loadingSettingsRef.current;
      // Loading-distance gate is *camera-distance* based — meaningful for
      // perspective cameras where distance correlates with on-screen
      // size. In ortho the camera is artificially dollied far away
      // (Viewer.tsx dollies to 1000 m for ortho vs 50 m for perspective)
      // so every node looks "far" regardless of whether it's inside the
      // ortho frustum. Applying the gate to ortho silently skipped every
      // node and produced "only a few points" loaded. The frustum cull
      // below already handles spatial filtering for ortho.
      const isOrthoCam = camera.type !== 'PerspectiveCamera';
      const effectiveLoadingDistance = isOrthoCam ? 0 : loadingDistance;

      cullMatrix.multiplyMatrices(camera.projectionMatrix, camera.matrixWorldInverse).multiply(pointCloud.matrixWorld);
      cullFrustum.setFromProjectionMatrix(cullMatrix);

      const children = node.children;
      for (let i = 0; i < children.length; i++) {
        const child = children[i];
        if (child === null) continue;

        const sphere = child.boundingSphere;
        const distance = sphere.center.distanceTo(cameraPosition);
        const radius = sphere.radius;

        // Frustum cull: skip loading nodes that are entirely off-screen.
        if (!cullFrustum.intersectsSphere(sphere)) continue;

        let projectionFactor = 0.0;
        const isOrtho = camera.type !== 'PerspectiveCamera';

        if (!isOrtho) {
          const perspective = camera as THREE.PerspectiveCamera;
          const fov = (perspective.getEffectiveFOV() * Math.PI) / 180.0; // accounts for zoom
          const slope = Math.tan(fov / 2.0);
          projectionFactor = halfHeight / (slope * distance);
        } else {
          const orthographic = camera as THREE.OrthographicCamera;
          projectionFactor = (2 * halfHeight * orthographic.zoom) / (orthographic.top - orthographic.bottom);
        }

        const screenPixelRadius = radius * projectionFactor;

        // Ortho's projectionFactor is distance-independent, so the same
        // `minNodePixelSize` threshold loads dramatically less detail than
        // it does for perspective at typical viewing distance. Halve it
        // for ortho to restore the LOD coverage the tool had before the
        // setting's default was bumped from 50 → 100 — without forcing
        // every user to drag the slider back down.
        const threshold = isOrtho ? pointCloud.minNodePixelSize / 2 : pointCloud.minNodePixelSize;
        if (screenPixelRadius < threshold) continue;

        // Loading-distance gate: when set, prevent refining into nodes
        // whose nearest point lies past the user's chosen radius — but
        // only when the node is already small on screen. Nodes that are
        // visually significant (large screenPixelRadius) should always be
        // refined so the user sees coherent structure at any zoom level.
        // Without this ordering, zooming out to 1000m with a 500m loading
        // distance would skip coarse children that cover the whole scene.
        if (
          effectiveLoadingDistance > 0 &&
          distance - radius > effectiveLoadingDistance &&
          screenPixelRadius < threshold * 4
        )
          continue;

        const weight = distance < radius ? Number.MAX_VALUE : screenPixelRadius + 1 / distance;

        priorityQueue.push(new QueueItem(queueItem.pointCloudIndex, weight, child, node));
      }
    };

    const make = (version: 'v1' | 'v2') => {
      const p = new Potree(version);
      // @ts-expect-error https://github.com/pnext/three-loader/pull/231
      p.updateChildVisibility = updateChildVisibility;
      return p;
    };

    return { v1: make('v1'), v2: make('v2') } as const;
  }, []);
  const [pointClouds, setPointClouds] = useState<PointCloudOctree[]>([]);

  // Keyed cache of loaded clouds, so toggling layers on/off only loads the
  // new ones and removes the gone ones — instead of unloading everything and
  // reloading from scratch on every layer-set change. Key includes `root` so
  // a layer whose root URL changed gets reloaded.
  const loadedRef = useRef<Map<string, PointCloudOctree>>(new Map());

  useEffect(() => {
    const abortController = new AbortController();

    const cacheKey = (layer: LayerData) => `${layer.id}::${layer.root ?? ''}`;
    const desiredKeys = new Set(layers.map(cacheKey));
    const cache = loadedRef.current;

    // Remove cached clouds that are no longer in the layer list (or whose
    // root changed). Existing clouds for unchanged layers stay loaded.
    let removed = false;
    for (const [key, pco] of cache) {
      if (desiredKeys.has(key)) continue;
      scene.remove(pco);
      cache.delete(key);
      removed = true;
    }

    const toLoad = layers.filter((l) => !cache.has(cacheKey(l)));

    const syncState = () => {
      // Preserve the order of `layers` so downstream consumers get a stable
      // order (e.g. AddPointToolWrapper uses pointClouds[0]).
      setPointClouds(layers.map((l) => cache.get(cacheKey(l))).filter((pco): pco is PointCloudOctree => !!pco));
    };

    const loadOne = (layer: LayerData) => {
      if (!layer.root) throw new Error('No root file specified for point cloud layer ' + layer.name);

      const isExternal = layer.root.startsWith('http');

      // Detect Potree version for this layer. Preferred source: the
      // `potreeVersion` field the backend writes onto `potreeMetadata`
      // (see server/background/src/functions/processPotree.ts). As a
      // fallback, sniff the root URL's filename. Legacy v2 layers that
      // lack the field default to v2.
      const meta = (layer.metadata ?? {}) as any;
      const potreeMeta = meta.potreeMetadata as any;
      const detectedFromUrl: 'v1' | 'v2' | null = layer.root.endsWith('cloud.js')
        ? 'v1'
        : layer.root.endsWith('metadata.json')
          ? 'v2'
          : null;
      const version: 'v1' | 'v2' = (potreeMeta?.potreeVersion as 'v1' | 'v2' | undefined) ?? detectedFromUrl ?? 'v2';
      const metadataFilename = version === 'v1' ? 'cloud.js' : 'metadata.json';

      // Build a full URL that points at the metadata file regardless of
      // whether the layer.root is a directory or already the file itself.
      const metadataUrl = layer.root.endsWith(metadataFilename)
        ? layer.root
        : `${layer.root.replace(/\/+$/, '')}/${metadataFilename}`;

      // If we have a cached metadata blob on the layer, replay it instead
      // of re-fetching. v2 stores the normalised v2 shape directly; v1
      // stores the normalised shape with the original cloud.js payload
      // under `.raw` — the library needs the raw v1 format.
      const cachedMetadata = potreeMeta ? (version === 'v1' ? potreeMeta.raw : potreeMeta) : undefined;

      const potree = version === 'v1' ? potrees.v1 : potrees.v2;

      return potree
        .loadPointCloud(
          metadataUrl,
          // Resolve relative node URLs against the cloud's own origin when
          // the layer was imported from an external server. For internally
          // hosted clouds (our S3 resources bucket) we keep the historical
          // behaviour of prefixing with VITE_RESOURCES_ENDPOINT.
          (relativeUrl) => {
            if (relativeUrl.startsWith('http')) return relativeUrl;
            if (isExternal) {
              // `layer.root` is the baseUrl (e.g. https://host/pointclouds/foo)
              // — library asks for things like "data/r" or "data/r0.bin".
              return `${layer.root!.replace(/\/+$/, '')}/${relativeUrl.replace(/^\/+/, '')}`;
            }
            return `${import.meta.env.VITE_RESOURCES_ENDPOINT}/${relativeUrl}`;
          },
          (input: RequestInfo, init?: RequestInit) => {
            if (cachedMetadata && String(input).endsWith(metadataFilename)) {
              const mockResponse = new Response(JSON.stringify(cachedMetadata), {
                status: 200,
                headers: { 'Content-Type': 'application/json' },
              });
              return Promise.resolve(mockResponse);
            }
            return fetch(input, {
              ...init,
              ...(isExternal ? {} : { credentials: 'include' as const }),
            });
          },
        )
        .then((pco) => {
          pco.up.set(0, 0, 1);

          // v2-only: sniff rgb presence from the octree loader's metadata
          // for a future UI hint. v1 (`loadPOC`) uses a different loader
          // without this shape, so skip the check entirely for v1.
          const v2Loader = pco.pcoGeometry.loader as NodeLoader | undefined;
          if (v2Loader?.metadata?.attributes?.find((a) => a.name === 'rgb')?.max?.some?.((v) => v > 0)) {
            // todo: remove rgb option
          }

          pco.material.minSize = 1;
          pco.material.maxSize = 16;
          pco.material.shape = PointShape.SQUARE;

          pco.minNodePixelSize = viewerSettings?.pointCloudMinNodePixelSize ?? 100;
          pco.frustumCulled = true;

          pco.material.pointOpacityType = PointOpacityType.ATTENUATED;
          pco.material.opacityAttenuation = 1000;

          if (viewerSettings?.pointCloudPointSizeType === 'FIXED') {
            pco.material.size = viewerSettings?.pointCloudPointSize || 1.0;
            pco.material.pointSizeType = PointSizeType.FIXED;
          } else {
            const spacing = pco.pcoGeometry.spacing;
            pco.material.size = 1.0 / spacing / 23;
            pco.material.pointSizeType = PointSizeType.ATTENUATED;
          }

          pco.userData.layerId = layer.id;

          // Override updateMaterial because of FOV bug in library. https://github.com/pnext/three-loader/pull/231
          pco.material.updateMaterial = function (
            octree: PointCloudOctree,
            visibleNodes: PointCloudOctreeNode[],
            camera: THREE.Camera,
            renderer: THREE.WebGLRenderer,
          ) {
            const material = this as PointCloudMaterial;
            const pixelRatio = renderer.getPixelRatio();

            if (camera.type === 'PerspectiveCamera') {
              const perspectiveCamera = camera as THREE.PerspectiveCamera;
              material.fov = perspectiveCamera.getEffectiveFOV() * (Math.PI / 180);
            } else {
              material.fov = Math.PI / 2; // will result in slope = 1 in the shader
            }

            const renderTarget = renderer.getRenderTarget();
            if (renderTarget !== null) {
              material.screenWidth = renderTarget.width;
              material.screenHeight = renderTarget.height;
            } else {
              material.screenWidth = renderer.domElement.clientWidth * pixelRatio;
              material.screenHeight = renderer.domElement.clientHeight * pixelRatio;
            }

            if (material.useDrawingBufferSize) {
              const helperVec2 = new THREE.Vector2();
              renderer.getDrawingBufferSize(helperVec2);
              material.screenWidth = helperVec2.width;
              material.screenHeight = helperVec2.height;
            }

            const maxScale = Math.max(octree.scale.x, octree.scale.y, octree.scale.z);
            material.spacing = octree.pcoGeometry.spacing * maxScale;

            const helperVec3 = new THREE.Vector3();
            material.octreeSize = octree.pcoGeometry.boundingBox.getSize(helperVec3).x;

            if (material.pointSizeType === PointSizeType.ADAPTIVE || material.pointColorType === PointColorType.LOD) {
              // Call the private method through type assertion
              (material as any).updateVisibilityTextureData(visibleNodes);
            }
          };

          return { layer, pco };
        });
    };

    if (toLoad.length === 0) {
      if (removed) syncState();
      return () => {
        abortController.abort();
      };
    }

    // Load each layer independently so a single failure (CORS, 404, bad
    // metadata) doesn't swallow the whole batch. Previously we used a
    // naive `Promise.all` with no `.catch`, so any load rejection was
    // silently lost and the user saw an empty viewer with zero network
    // activity because nothing ever made it into the scene.
    const loadResults = toLoad.map((layer) =>
      loadOne(layer).then(
        (r) => ({ ok: true as const, layer, pco: r.pco }),
        (err) => ({ ok: false as const, layer, err }),
      ),
    );

    Promise.all(loadResults).then((results) => {
      if (abortController.signal.aborted) {
        // Effect was torn down before loads finished; drop these orphans so
        // they never get added to the scene.
        return;
      }
      let added = 0;
      for (const r of results) {
        if (!r.ok) {
          console.error(`[PointCloud] Failed to load layer "${r.layer.name}" (${r.layer.id}):`, r.err);
          continue;
        }
        const key = cacheKey(r.layer);
        // Belt and suspenders: if a concurrent run already cached this key
        // (e.g. strict-mode double invoke), prefer the existing instance.
        if (cache.has(key)) continue;
        cache.set(key, r.pco);
        scene.add(r.pco);
        added++;
      }
      if (added > 0 || removed) syncState();
    });

    return () => {
      abortController.abort();
    };
  }, [layers, potrees, scene]);

  // Tear down all loaded clouds when the component unmounts. Kept separate
  // from the diff effect above so a layer-list change doesn't trigger a full
  // teardown.
  useEffect(() => {
    return () => {
      const cache = loadedRef.current;
      cache.forEach((pco) => scene.remove(pco));
      cache.clear();
    };
  }, [scene]);

  // Sync loaded point clouds to shared atom so measuring tools can access them
  useEffect(() => {
    setPointCloudTargets(pointClouds);
    return () => setPointCloudTargets([]);
  }, [pointClouds, setPointCloudTargets]);

  useFrame(() => {
    // When using an orthographic camera the adaptive point-size formula
    // (designed for perspective projection) produces oversized points for
    // geometry that is close in world-space.  Switch every PCO to FIXED
    // sizing with a zoom-derived size so points stay visually consistent.
    const isOrtho = camera instanceof THREE.OrthographicCamera;
    pointClouds.forEach((pc) => {
      if (isOrtho) {
        if (pc.material.pointSizeType !== PointSizeType.FIXED) {
          pc.material.pointSizeType = PointSizeType.FIXED;
        }
        // Use user-configurable ortho point size, scaled with zoom for natural feel.
        const baseSize = viewerSettings?.orthoPointSize ?? 2;
        const orthoSize = Math.max(1, Math.min(baseSize * 3, camera.zoom * (baseSize * 0.075)));
        pc.material.size = orthoSize;

        // Attenuated opacity is distance-based and breaks with orthographic
        // projection — use fixed full opacity instead.
        if (pc.material.pointOpacityType !== PointOpacityType.FIXED) {
          pc.material.pointOpacityType = PointOpacityType.FIXED;
          pc.material.opacity = 1;
        }
      } else {
        // Restore user-chosen settings when switching back to perspective
        if (viewerSettings?.pointCloudPointSizeType === 'FIXED') {
          if (pc.material.pointSizeType !== PointSizeType.FIXED) {
            pc.material.pointSizeType = PointSizeType.FIXED;
          }
          pc.material.size = viewerSettings?.pointCloudPointSize || 1.0;
        } else {
          if (pc.material.pointSizeType !== PointSizeType.ATTENUATED) {
            pc.material.pointSizeType = PointSizeType.ATTENUATED;
          }
          const spacing = pc.pcoGeometry.spacing;
          pc.material.size = 1.0 / spacing / 23;
        }

        // Restore attenuated opacity for perspective camera
        if (pc.material.pointOpacityType !== PointOpacityType.ATTENUATED) {
          pc.material.pointOpacityType = PointOpacityType.ATTENUATED;
          pc.material.opacityAttenuation = 1000;
        }
      }
    });

    // Dispatch `updatePointClouds` per version: each Potree instance owns its
    // own LRU and must only see the clouds it loaded. We detect the loader
    // type (v1 uses BinaryLoader, v2 uses NodeLoader) to partition the list.
    const v1Clouds: PointCloudOctree[] = [];
    const v2Clouds: PointCloudOctree[] = [];
    for (const pc of pointClouds) {
      // v2's loader exposes a `metadata` field (NodeLoader); v1's BinaryLoader
      // does not — use that as the discriminator so we don't need to store
      // the version on userData.
      const isV2 = !!(pc.pcoGeometry.loader as NodeLoader | undefined)?.metadata;
      (isV2 ? v2Clouds : v1Clouds).push(pc);
    }
    if (v1Clouds.length) potrees.v1.updatePointClouds(v1Clouds, camera, renderer);
    if (v2Clouds.length) potrees.v2.updatePointClouds(v2Clouds, camera, renderer);
  });

  useEffect(() => {
    pointClouds.forEach((pc) => {
      pc.minNodePixelSize = viewerSettings?.pointCloudMinNodePixelSize ?? 100;
    });
  }, [pointClouds, viewerSettings?.pointCloudMinNodePixelSize]);

  useEffect(() => {
    // Point budget is per-Potree-instance; keep both versions in sync with
    // the user's setting.
    potrees.v1.pointBudget = viewerSettings?.pointCloudPointBudget;
    potrees.v2.pointBudget = viewerSettings?.pointCloudPointBudget;
    // Point size type/size is managed by useFrame when ortho is active,
    // so only apply user settings for perspective cameras.
    if (!(camera instanceof THREE.OrthographicCamera)) {
      pointClouds.forEach((pc) => {
        if (viewerSettings?.pointCloudPointSizeType === 'FIXED') {
          pc.material.pointSizeType = PointSizeType.FIXED;
          pc.material.size = viewerSettings?.pointCloudPointSize || 1.0;
        } else {
          pc.material.pointSizeType = PointSizeType.ATTENUATED;
          const spacing = pc.pcoGeometry.spacing;
          pc.material.size = 1.0 / spacing / 23;
        }
      });
    }
  }, [
    pointClouds,
    camera,
    viewerSettings?.pointCloudPointBudget,
    viewerSettings?.pointCloudPointSizeType,
    viewerSettings?.pointCloudPointSize,
    potrees,
  ]);

  useEffect(() => {
    pointClouds.forEach((pc) => {
      const layerSetting = layerSettings[pc.userData.layerId];

      if (!layerSetting) return;

      pc.material.opacity = visible ? 1 : 0;

      pc.material.pointColorType = layerSetting.pointCloudMaterialColorSource || 0;
      // @ts-ignore
      pc.material.gradient = gradients[layerSetting.pointCloudMaterialColor];

      if (layerSetting.pointCloudMaterialColorSource === 1 && layerSetting.pointCloudFixedColor) {
        pc.material.color = new THREE.Color(layerSetting.pointCloudFixedColor);
      }

      if (pc.material.pointColorType === PointColorType.CLASSIFICATION) {
        pc.material.useFilterByClassification = true;
        const classification: IClassification = { DEFAULT: new THREE.Vector4(0, 0, 0, 1) };
        layerSetting.pointCloudClasses.forEach((values, i) => {
          if (!values) return;
          const { color, isEnabled } = values;
          pc.material.classificationFilter[i] = isEnabled;
          if (color) classification[i] = new THREE.Vector4(...new THREE.Color(color).toArray(), 1);
        });
        pc.material.classification = classification;
      } else {
        pc.material.useFilterByClassification = false;
      }

      if (layerSetting.pointCloudMaterialColorSource === PointColorType.CUSTOM_SCALAR) {
        const layer = layers.find((l) => l.id === pc.userData.layerId);
        const layerMetadata = (layer?.metadata ?? {}) as Record<string, any>;
        pc.material.pointColorType = PointColorType.CUSTOM_SCALAR;
        pc.material.customAttributeName = (layerMetadata.scalarAttribute as string) || 'Deviation';
        const fallbackRange = (layerMetadata.deviationRange as [number, number] | undefined) ?? [-0.05, 0.05];
        const overrideMin = layerSetting.scalarRangeMin;
        const overrideMax = layerSetting.scalarRangeMax;
        const min = overrideMin != null ? overrideMin : fallbackRange[0];
        const max = overrideMax != null ? overrideMax : fallbackRange[1];
        pc.material.customScalarRange = [min, max];
        // @ts-ignore
        pc.material.gradient = gradients[layerSetting.pointCloudMaterialColor ?? 'SPECTRAL'] ?? gradients.SPECTRAL;
      }

      pc.material.updateShaderSource();
    });
  }, [pointClouds, layerSettings, visible]);

  if (!pointClouds || pointClouds.length === 0) return null;

  return (
    <>
      {project?.id && <AddPointToolWrapper origin={pointClouds[0].position} targets={pointClouds} project={project} />}
      {pickedPoint?.position && <PointX scale={0.1} position={pickedPoint.position} />}
      <PointCloudBoundingBoxes layers={layers} />
    </>
  );
};

// Wrapper component to handle add point mode state
const AddPointToolWrapper = ({
  origin,
  targets,
  project,
}: {
  origin: THREE.Vector3;
  targets: PointCloudOctree[];
  project: GetProjectQuery['project'];
}) => {
  const addPointMode = useAtomValue(addPointModeAtom);

  if (!addPointMode.enabled || !addPointMode.targetLayerId) return null;

  return (
    <AddPointTool
      origin={origin}
      targets={targets}
      enabled={addPointMode.enabled}
      srid={project.srid}
      targetLayerId={addPointMode.targetLayerId}
    />
  );
};

const gradients = {
  GRAYSCALE: [
    [0, new THREE.Color(0)],
    [1, new THREE.Color(16777215)],
  ],
  RAINBOW: [
    [0, new THREE.Color(4587702)],
    [0.16666666666666666, new THREE.Color(255)],
    [0.3333333333333333, new THREE.Color(65535)],
    [0.5, new THREE.Color(65280)],
    [0.6666666666666666, new THREE.Color(16776960)],
    [0.8333333333333334, new THREE.Color(16753408)],
    [1, new THREE.Color(16711680)],
  ],
  SPECTRAL: [
    [0, new THREE.Color(6114978)],
    [0.1, new THREE.Color(3311549)],
    [0.2, new THREE.Color(6734501)],
    [0.3, new THREE.Color(11263395)],
    [0.4, new THREE.Color(15136152)],
    [0.5, new THREE.Color(16777150)],
    [0.6, new THREE.Color(16703371)],
    [0.7, new THREE.Color(16625249)],
    [0.8, new THREE.Color(16018754)],
    [0.9, new THREE.Color(13974862)],
    [1, new THREE.Color(10289217)],
  ],
  PLASMA: [
    [0, new THREE.Color(3998619)],
    [0.1, new THREE.Color(6422694)],
    [0.2, new THREE.Color(8717990)],
    [0.3, new THREE.Color(10887063)],
    [0.4, new THREE.Color(12532098)],
    [0.5, new THREE.Color(13980525)],
    [0.6, new THREE.Color(15167066)],
    [0.7, new THREE.Color(16092485)],
    [0.8, new THREE.Color(16559409)],
    [0.9, new THREE.Color(16503076)],
    [1, new THREE.Color(15726625)],
  ],
  YELLOW_GREEN: [
    [0, new THREE.Color(2705496)],
    [0.1, new THREE.Color(2251371)],
    [0.2, new THREE.Color(1011324)],
    [0.3, new THREE.Color(33415)],
    [0.4, new THREE.Color(38286)],
    [0.5, new THREE.Color(2271631)],
    [0.6, new THREE.Color(4897931)],
    [0.7, new THREE.Color(7458436)],
    [0.8, new THREE.Color(10215291)],
    [0.9, new THREE.Color(13233523)],
    [1, new THREE.Color(16448110)],
  ],
  VIRIDIS: [
    [0, new THREE.Color(4456787)],
    [0.1, new THREE.Color(4727668)],
    [0.2, new THREE.Color(4211591)],
    [0.3, new THREE.Color(3432077)],
    [0.4, new THREE.Color(2717838)],
    [0.5, new THREE.Color(2134156)],
    [0.6, new THREE.Color(2271364)],
    [0.7, new THREE.Color(4505200)],
    [0.8, new THREE.Color(7983441)],
    [0.9, new THREE.Color(12377638)],
    [1, new THREE.Color(16639780)],
  ],
  INFERNO: [
    [0, new THREE.Color(1247796)],
    [0.1, new THREE.Color(3737954)],
    [0.2, new THREE.Color(6230638)],
    [0.3, new THREE.Color(8724587)],
    [0.4, new THREE.Color(11087454)],
    [0.5, new THREE.Color(13320521)],
    [0.6, new THREE.Color(15096878)],
    [0.7, new THREE.Color(16220944)],
    [0.8, new THREE.Color(16494098)],
    [0.9, new THREE.Color(16112459)],
    [1, new THREE.Color(16514724)],
  ],
};
